/**
 * Display formatting.
 *
 * Money and the other numeric columns arrive as strings and are formatted
 * as strings: digits are grouped and padded, never parsed into a float and
 * printed back. A count is a plain number and goes through the locale.
 */

/** A timestamp as a person reads it: 09/16/2026, 2:05 PM. Em dash when absent. */
export function dateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return value;
  const day = String(at.getDate()).padStart(2, '0');
  const month = String(at.getMonth() + 1).padStart(2, '0');
  const hours = at.getHours();
  const suffix = hours < 12 ? 'AM' : 'PM';
  const hour = hours % 12 === 0 ? 12 : hours % 12;
  const minutes = String(at.getMinutes()).padStart(2, '0');
  return `${month}/${day}/${at.getFullYear()}, ${hour}:${minutes} ${suffix}`;
}

/** A YYYY-MM-DD day as 09/16/2026, untouched by any time zone. Em dash when absent. */
export function day(value: string | null | undefined): string {
  if (!value) return '—';
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
  if (!match) return value;
  return `${match[2]}/${match[3]}/${match[1]}`;
}

/** A YYYY-MM month as Sep 2026. */
export function monthLabel(value: string): string {
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  if (!match) return value;
  const names = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${names[Number(match[2]) - 1] ?? match[2]} ${match[1]}`;
}

function groupDigits(digits: string): string {
  return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

/**
 * A numeric string with grouped thousands and a fixed number of decimals:
 * "1234.5" with 2 decimals is "1,234.50". Truncates rather than rounds a
 * longer fraction, which is deliberate: this is display, not arithmetic,
 * and a rounded figure would disagree with the CSV.
 */
export function decimal(value: string | null | undefined, decimals?: number): string {
  if (value === null || value === undefined || value === '') return '—';
  const match = /^(-)?(\d+)(?:\.(\d+))?$/.exec(value);
  if (!match) return value;
  const sign = match[1] ?? '';
  const whole = groupDigits(match[2] as string);
  let fraction = match[3] ?? '';
  if (decimals !== undefined) {
    fraction = fraction.padEnd(decimals, '0').slice(0, decimals);
  }
  return fraction ? `${sign}${whole}.${fraction}` : `${sign}${whole}`;
}

/** "$1,234.56" from "1234.56". */
export function money(value: string | null | undefined): string {
  if (value === null || value === undefined || value === '') return '—';
  const text = decimal(value, 2);
  return text.startsWith('-') ? `-$${text.slice(1)}` : `$${text}`;
}

/** A count with grouped thousands. Em dash when absent. */
export function integer(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return groupDigits(String(Math.trunc(value)));
}

/**
 * The one place a numeric string becomes a float.
 *
 * Money and other `numeric` columns travel as strings so nothing rounds them by
 * accident (see the module header). A chart, though, has to plot a number: an
 * SVG bar height is a float. This is the documented boundary where that happens,
 * and the only one — every tooltip and table cell still formats from the exact
 * string with `money`/`decimal`, so what a reader sees is never the rounded
 * plot value. A null, blank, or unparseable value plots as 0.
 */
export function toPlotNumber(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === '') return 0;
  const n = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

/** Bytes as a person reads them. */
export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
